import * as dateFnsCompat from 'utils/date-fns-compat';

import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { contentType, cpdPointStatus, dateFormats } from '../../../../constants';
import { fullName, getIndustryLabelForCpdCategory } from '../../../../services';
import { adminActions } from '../../../../state/modules/admin';
import { downloadBlobStreamFile, getBlobStream, getCsvStream, writeDataToCsvStream } from '../../../../utils';
import { DateFormField, GeneralTable, LoadingButton } from '../../../components';

const uuidv1 = require('uuid/v1');

const TaskStatus = {
  created: 'created',
  waiting: 'waiting',
  finished: 'finished'
}

const UserEngagement = ({
  allCpdCategories,
  totalUserCount,
  estimationTasks,
  startEstimation,
  estimations,
  isEstimatingTasks,
  estimationProgress,
  data,
  startDate,
  setStartDate,
  cpdCategorys,
  exportCsv,
  industriesWithCpdCategorys
}) => {
  const dataReady = (data.length > 0) && (!isEstimatingTasks)

  const columns = [
    {
      key: 'userInfo',
      name: `User`
    },
    {
      key: 'cpd',
      name: 'CPD'
    },
    {
      key: 'pointsByCpdCategory',
      name: 'Points By CPD Category'
    },
    {
      key: 'pointsByContentType',
      name: 'Points By Content Type'
    },
    {
      key: 'itemsByContentType',
      name: 'Items By Content Type'
    }
  ]

  const rows = fp.map(
    ({
      userInfo,
      cpd,
      pointsByCpdCategory,
      pointsByContentType,
      itemsByContentType
    }) => {
      return {
        userInfo: {
          value: <div>
            <div>{`Name: ${userInfo.fullName}`}</div>
            <div>{`Email: ${userInfo.email}`}</div>
            <div>{`Subscription: ${userInfo.subscription}`}</div>
            <div>{`Industry: ${userInfo.industry}`}</div>
          </div>
        },
        cpd: {
          value: <div>
            <div>{`Goal: ${cpd.goal}`}</div>
            <div>{`Completed: ${cpd.completed}`}</div>
            {
              startDate ?
                <div>{`Completed Since ${dateFnsCompat.format(startDate, dateFormats.ShortDate)}: ${cpd.completed}`}</div> :
                null
            }
            <div>{`CPD Date: ${cpd.cpdEndDate}`}</div>
          </div>
        },
        pointsByCpdCategory: {
          value: <div>
            {
              fp.flow(
                fp.toPairs,
                fp.map(
                  ([cpdCategoryId, points]) => {
                    const total = fp.sumBy('points')(points)
                    const cpdCategoryIdParsed = parseInt(cpdCategoryId, 10)
                    const industries = getIndustryLabelForCpdCategory(cpdCategoryIdParsed, industriesWithCpdCategorys)
                    const cpdCategoryName = fp.flow(
                      fp.find(
                        ({ id }) => id === cpdCategoryIdParsed
                      ),
                      fp.getOr('')('name'),
                      he.decode
                    )(cpdCategorys)
                    return <div key={cpdCategoryId}>{`${cpdCategoryName}${industries} - ${total}`}</div>
                  }
                )
              )(pointsByCpdCategory)
            }
          </div>
        },
        pointsByContentType: {
          value: <div>
            {
              fp.flow(
                fp.toPairs,
                fp.map(
                  ([contentType, points]) => {
                    const total = fp.sumBy('points')(points)
                    return <div key={contentType}>{`${contentType} - ${total}`}</div>
                  }
                )
              )(pointsByContentType)
            }
          </div>
        },
        itemsByContentType: {
          value: <div>
            {
              fp.flow(
                fp.toPairs,
                fp.map(
                  ([contentType, items]) => {
                    const count = items.length
                    return <div key={contentType}>{`${contentType} - ${count}`}</div>
                  }
                )
              )(itemsByContentType)
            }
          </div>
        }
      }
    }
  )(data)

  return <div className='base-layout'>
    <section className='layout--header'>
      <Grid>
        <Row className='button-gutter'>
          <Col xs={12}>
            <h1>User Engagement</h1>
          </Col>
        </Row>
      </Grid>
    </section>
    <section id='section--admin-user-engagement' className='layout--section'>
      <Grid>
        <Row className='no-gutter sectionHeader'>
          {
            isEstimatingTasks ? `Estimation Progress (${estimationProgress})` : null
          }
          <LoadingButton loading={isEstimatingTasks} onClick={startEstimation} text={`Estimate Engagement for ${totalUserCount} Users`} />
        </Row>

        {
          dataReady ?
            <Row className='no-gutter sectionHeader'>
              <Col>
                <form>
                  <DateFormField {...{
                    id: 'timeEarned',
                    label: 'Since',
                    onChange: setStartDate,
                    dateProps: {
                      minYear: (new Date()).getFullYear() - 2,
                      futureYears: 0
                    },
                    value: startDate
                  }} />
                </form>
              </Col>
              <Col>
                <Button onClick={exportCsv}>Download</Button>
              </Col>
              <Col>
                <GeneralTable className='user-engagement-table' columns={columns} rows={rows} />
              </Col>
            </Row> : null
        }

      </Grid>
    </section>
  </div>
}

const mapStateToProps = (state) => ({
  userIds: state.admin.userIds,
  isEstimating: state.admin.isEstimatingUserEngagement,
  estimations: state.admin.userEngagementEstimations,
  cpdCategorys: state.cpdCategorys.cpdCategorys,
  industriesWithCpdCategorys: state.industry.industriesWithCpdCategorys
})
const mapDispatchToProps = (dispatch) => ({
  getAllUserIds: bindActionCreators(adminActions.admin.getAllUserIds, dispatch),
  estimate: bindActionCreators(adminActions.admin.estimateUserEngagement, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(
    ({ userIds }) => ({
      totalUserCount: userIds.length
    })
  ),
  withStateHandlers(
    {
      estimationTasks: [],
      startDate: undefined
    },
    {
      setStartDate: () => (startDate) => ({ startDate }),
      startEstimation: (state, { userIds }) => () => ({
        estimationTasks: fp.flow(
          fp.chunk(100),
          fp.map(
            (ids) => {
              return {
                id: uuidv1(),
                status: TaskStatus.created,
                params: ids
              }
            }
          )
        )(userIds)
      }),
      runTask: ({ estimationTasks }) => (taskToRun) => ({
        estimationTasks: fp.map(
          (task) => task.id === taskToRun.id ? {
            ...task,
            status: TaskStatus.waiting
          } : task
        )(estimationTasks)
      }),
      finishWaitingTasks: ({ estimationTasks }) => () => ({
        estimationTasks: fp.map(
          (task) => task.status === TaskStatus.waiting ? {
            ...task,
            status: TaskStatus.finished
          } : task
        )(estimationTasks)
      })
    }
  ),
  withProps(
    ({ estimationTasks }) => ({
      createdTasks: fp.filter(
        ({ status }) => status === TaskStatus.created
      )(estimationTasks),
      waitingTasks: fp.filter(
        ({ status }) => status === TaskStatus.waiting
      )(estimationTasks),
      finishedTasks: fp.filter(
        ({ status }) => status === TaskStatus.finished
      )(estimationTasks),
      isEstimatingTasks: !fp.all(
        ({ status }) => status === TaskStatus.finished
      )(estimationTasks) && estimationTasks.length > 0
    })
  ),
  withProps(
    ({ estimationTasks, finishedTasks }) => ({
      estimationProgress: `${finishedTasks.length}/${estimationTasks.length}`
    })
  ),
  lifecycle({
    componentDidMount() {
      const {
        getAllUserIds
      } = this.props

      getAllUserIds()
    },
    componentDidUpdate(prevProps) {
      const {
        createdTasks,
        waitingTasks,
        runTask,
        isEstimating,
        finishWaitingTasks,
        estimate
      } = this.props
      if (fp.isEmpty(waitingTasks)) {
        const pendingTask = fp.first(createdTasks)
        if (pendingTask) {
          runTask(pendingTask)
          estimate(pendingTask.params)
        }
      }
      if (!isEstimating && prevProps.isEstimating) {
        finishWaitingTasks()
      }
    }
  }),
  withProps(
    ({ estimations, startDate }) => ({
      data: fp.flow(
        fp.map(
          ({ user, stat, trainingPlans, cpdPoints, subscription }) => {
            const validCpdPoints = fp.filter(
              ({ status, timeEarned }) => {
                const approved = (status === cpdPointStatus.approved)
                const withinTimeRange = (!startDate || (dateFnsCompat.parse(timeEarned) > startDate))
                const included = approved && withinTimeRange
                return included
              }
            )(cpdPoints)
            const extendedCpdPointAllocations = fp.flow(
              fp.map(
                ({ cpdPointAllocation, contentType, timeEarned }) => {
                  return fp.map(
                    (allocation) => {
                      return {
                        ...allocation,
                        contentType,
                        timeEarned: dateFnsCompat.format(dateFnsCompat.parse(timeEarned), dateFormats.ShortDate)
                      }
                    }
                  )(cpdPointAllocation)
                }
              ),
              fp.flatten
            )(validCpdPoints)

            const userInfo = {
              id: user.id,
              email: user.email,
              fullName: fullName(user),
              subscription: subscription ? fp.get('status')(subscription) : 'None',
              industry: fp.flow(
                fp.map('industry.name')
              )(trainingPlans).join(', ')
            }
            const cpd = {
              goal: stat.primaryStat.goal,
              completed: stat.primaryStat.validCpd,
              sinceStartDate: fp.sumBy('points')(extendedCpdPointAllocations),
              cpdEndDate: `${fp.get('[0].cpdMonth')(trainingPlans)}/${fp.get('[0].cpdDay')(trainingPlans)}`
            }
            const pointsByCpdCategory = fp.flow(
              fp.groupBy('cpdCategoryId')
            )(extendedCpdPointAllocations)
            const pointsByContentType = fp.flow(
              fp.groupBy('contentType')
            )(extendedCpdPointAllocations)
            const itemsByContentType = fp.flow(
              fp.map(
                ({ contentType, userCpdPointId }) => ({ contentType, userCpdPointId })
              ),
              fp.uniqBy('userCpdPointId'),
              fp.groupBy('contentType')
            )(validCpdPoints)

            return {
              userInfo,
              cpd,
              pointsByCpdCategory,
              pointsByContentType,
              itemsByContentType
            }
          }
        )
      )(estimations)
    })
  ),
  withHandlers({
    exportCsv: ({ cpdCategorys, data }) => () => {
      const sortedCpdCategorys = fp.sortBy('name')(cpdCategorys)
      const columns = [
        'User Id',
        'Name',
        'Email',
        'Status',
        'Industry',
        'Total Required',
        'Total Completed',
        'Tota lCompleted Last Period',
        'Cpd End Date'
      ].concat(
        fp.map('name')(sortedCpdCategorys)
      ).concat(
        fp.values(contentType)
      ).concat(
        fp.flow(
          fp.values,
          fp.map(
            (contentType) => `${contentType}-Count`
          )
        )(contentType)
      )
      const rows = fp.map(
        ({ userInfo, cpd, pointsByCpdCategory, pointsByContentType, itemsByContentType }) => {
          const userData = [
            userInfo.id,
            userInfo.fullName,
            userInfo.email,
            userInfo.subscription,
            userInfo.industry
          ]
          const cpdData = [
            cpd.goal,
            cpd.completed,
            cpd.sinceStartDate,
            cpd.cpdEndDate
          ]
          const cpdCategoryData = fp.map(
            ({ id }) => {
              return fp.flow(
                fp.get(id),
                fp.sumBy('points')
              )(pointsByCpdCategory)
            }
          )(sortedCpdCategorys)
          const contentTypeData = fp.flow(
            fp.values,
            fp.map(
              (contentType) => {
                return fp.flow(
                  fp.get(contentType),
                  fp.sumBy('points')
                )(pointsByContentType)
              }
            )
          )(contentType)
          const contentTypeCountData = fp.flow(
            fp.values,
            fp.map(
              (contentType) => {
                return fp.flow(
                  fp.get(contentType),
                  fp.getOr(0)('length')
                )(itemsByContentType)
              }
            )
          )(contentType)
          return userData.concat(cpdData).concat(cpdCategoryData).concat(contentTypeData).concat(contentTypeCountData)
        }
      )(data)
      downloadBlobStreamFile(writeDataToCsvStream(getCsvStream({
        header: true,
        columns
      }), rows).pipe(getBlobStream()), 'user-engagement.csv');
    }
  })
)(UserEngagement)


// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/user-engagement/user-engagement.js